import React, {
  useState,
  useEffect,
  useMemo,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import CandidateForm from "../components/CandidateForm";
import TableView from "../components/crm/TableView";
import SearchInput from "../components/SearchInput";
import { ViewSwitcher, ViewMode } from "../components/ViewSwitcher";
import { Pagination } from "../components/Pagination";
import { EmptyState, ErrorState } from "../components/ListStates";
import { FilterSelect } from "../components/FilterSelect";
import { ClearFiltersButton } from "../components/ClearFiltersButton";
import {
  CandidateStatusBadge,
  CandidateStatusBadgeWithActions,
} from "../components/CandidateStatusControl";
import SplitActionButton from "../components/SplitActionButton";
import { StatusKanbanBoard } from "../components/boards";
import { CandidateBoardCard } from "../components/boards/CandidateBoardCard";
import {
  CANDIDATE_STATUS_CONFIG,
  CANDIDATE_KANBAN_COLUMNS,
  CANDIDATE_BOARD_HIDDEN_BY_DEFAULT,
  CANDIDATE_ARCHIVE_REASONS,
  CANDIDATE_VALID_TRANSITIONS,
} from "../components/StatusControl/configs/candidateConfig";
import { Chip, Box, Popover, Tooltip } from "@mui/material";
import { getCurrentUser } from "../utils/auth";
import {
  MapPin,
  ExternalLink,
  Bot,
  Info,
  Upload,
  Shield,
  Clock,
  AlertTriangle,
  Trash2,
  Calendar,
} from "lucide-react";
import AppCountDropdown from "../components/AppCountDropdown";
import "./CandidatesList.css";

interface Candidate {
  id: number;
  status: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  location?: string;
  main_skill?: string;
  seniority?: string;
  role_type?: string;
  expected_rate?: number;
  decision?: string;
  source?: string;
  tags?: string | string[];
  summary?: string;
  candidate_code?: string;
  similarity_score?: number;
  owner_name?: string;
  responsible_recruiter_name?: string;
  kam_names?: string;
  gdpr_consent_status?: string;
  active_application_count?: number;
  most_advanced_app_status?: string;
  created_at?: string;
  updated_at?: string;
  // Index signature for TableView compatibility
  [key: string]: unknown;
}

interface CandidatesResponse {
  data?: Candidate[];
  unindexed?: Candidate[];
  total?: number;
}

interface CandidateFilters {
  seniority: string;
  status: string;
  decision: string;
  main_skill: string;
  source: string;
}

interface CurrentUser {
  id?: number;
  role?: string;
}

// Row type compatible with TableView's RowData
interface TableRowData {
  id: string | number;
  [key: string]: unknown;
}

interface TableColumn {
  id: string;
  label: string;
  render: (value: unknown, row: TableRowData) => React.ReactNode;
}

const CandidatesList: React.FC = () => {
  const { t } = useTranslation(["recruiting", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [useAISearch, setUseAISearch] = useState<boolean>(false);
  const [filters, setFilters] = useState<CandidateFilters>({
    seniority: "",
    status: "",
    decision: "",
    main_skill: "",
    source: "",
  });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [editingCandidate, setEditingCandidate] = useState<Candidate | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [calendarAnchor, setCalendarAnchor] = useState<{
    el: HTMLElement;
    candidate: Candidate;
  } | null>(null);

  // Get user role from auth utility
  const user = (getCurrentUser() || {}) as CurrentUser;
  const isTechChecker = user.role === "tech_checker";

  // Fetch dynamic filter options from DB
  const { data: filterMetadata } = useQuery(
    "candidateFilterMetadata",
    async () => {
      const response = await recruitingAPI.getCandidateFilterMetadata();
      return response.data;
    },
    { staleTime: 5 * 60 * 1000 },
  );

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch candidates
  const {
    data: candidatesData,
    isLoading,
    error,
  } = useQuery<{ candidates: Candidate[]; total: number }>(
    ["candidates", debouncedSearch, filters, currentPage, useAISearch],
    async () => {
      if (useAISearch && debouncedSearch.trim()) {
        const response = (await recruitingAPI.semanticSearch(
          debouncedSearch,
          20,
        )) as CandidatesResponse;
        return {
          candidates: response.data || [],
          total: response.data?.length || 0,
        };
      } else {
        const params: Record<string, string | number | boolean> = {
          q: debouncedSearch,
          page: currentPage,
          limit: 20,
          ...filters,
        };
        // Remove empty filters
        Object.keys(params).forEach((key) => {
          if (
            params[key] === "" ||
            params[key] === null ||
            params[key] === undefined
          ) {
            delete params[key];
          }
        });
        const response = (await recruitingAPI.getCandidates(
          params,
        )) as CandidatesResponse;
        return {
          candidates: response.data || [],
          total: response.total || response.data?.length || 0,
        };
      }
    },
    { keepPreviousData: true, enabled: viewMode !== "board" },
  );

  const candidates = candidatesData?.candidates || [];

  // Delete mutation
  const deleteMutation = useMutation(
    async (id: number) => await recruitingAPI.deleteCandidate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates");
      },
    },
  );

  // Board view: fetch all candidates (no pagination)
  const { data: boardCandidatesData, isLoading: boardLoading } = useQuery<{
    candidates: Candidate[];
  }>(
    ["candidates-board", debouncedSearch, filters],
    async () => {
      const params: Record<string, string | number | boolean> = {
        q: debouncedSearch,
        limit: 9999,
        ...filters,
      };
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });
      const response = (await recruitingAPI.getCandidates(
        params,
      )) as CandidatesResponse;
      return { candidates: response.data || [] };
    },
    { enabled: viewMode === "board", staleTime: 30000 },
  );

  const boardCandidates = boardCandidatesData?.candidates || [];

  // Board mutations
  const boardStatusMutation = useMutation(
    async ({
      id,
      status,
      note,
    }: {
      id: number;
      status: string;
      note: string;
    }) => {
      await recruitingAPI.updateCandidateStatus(id, { status, note });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates-board");
        queryClient.invalidateQueries("candidates");
      },
      onError: () => {
        queryClient.invalidateQueries("candidates-board");
        alert(
          t("errors.statusChangeFailed", {
            ns: "common",
            defaultValue:
              "Failed to update candidate status. Please try again.",
          }),
        );
      },
    },
  );

  const boardArchiveMutation = useMutation(
    async ({
      id,
      data,
    }: {
      id: number;
      data: {
        archive_reason: string;
        archive_reason_note: string | null;
        note?: string;
      };
    }) => {
      await recruitingAPI.archiveCandidate(id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("candidates-board");
        queryClient.invalidateQueries("candidates");
      },
      onError: () => {
        queryClient.invalidateQueries("candidates-board");
        alert(
          t("errors.archiveFailed", {
            ns: "common",
            defaultValue: "Failed to archive candidate. Please try again.",
          }),
        );
      },
    },
  );

  const handleFilterChange = (
    filterName: keyof CandidateFilters,
    value: string,
  ): void => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const hasActiveFilters =
    filters.seniority || filters.status || filters.main_skill || filters.source;

  const clearAllFilters = (): void => {
    setFilters({
      seniority: "",
      status: "",
      decision: "",
      main_skill: "",
      source: "",
    });
    setCurrentPage(1);
  };

  const handleCreate = (): void => {
    setEditingCandidate(null);
    setShowCreateModal(true);
  };

  const handleDelete = async (
    candidateId: number,
    candidateName: string,
  ): Promise<void> => {
    if (
      window.confirm(
        t("confirmations.deleteConfirmation", { ns: "common" }) +
          ` "${candidateName}"?`,
      )
    ) {
      try {
        await deleteMutation.mutateAsync(candidateId);
      } catch (err) {
        console.error("Error deleting candidate:", err);
        alert(t("candidates.errors.deleteFailed"));
      }
    }
  };

  const handleSave = (): void => {
    setShowCreateModal(false);
    setEditingCandidate(null);
    queryClient.invalidateQueries("candidates");
  };

  const handleCancel = (): void => {
    setShowCreateModal(false);
    setEditingCandidate(null);
  };

  const handleView = (candidateId: number): void => {
    navigate(`/candidates/${candidateId}`);
  };

  const formatTags = (tags: string | string[] | undefined): string[] => {
    if (!tags || (Array.isArray(tags) && tags.length === 0)) return [];
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "string")
      return tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    return [];
  };

  const GDPR_STATUS_CONFIG: Record<
    string,
    {
      label: string;
      description: string;
      bg: string;
      color: string;
      icon: React.ReactNode;
    }
  > = {
    none: {
      label: t("candidates.gdprStatus.noConsent"),
      description: t("candidates.gdprStatus.desc.noConsent"),
      bg: "#f8d7da",
      color: "#721c24",
      icon: <AlertTriangle size={12} />,
    },
    pending: {
      label: t("candidates.gdprStatus.pending"),
      description: t("candidates.gdprStatus.desc.pending"),
      bg: "#fff3cd",
      color: "#856404",
      icon: <Clock size={12} />,
    },
    given: {
      label: t("candidates.gdprStatus.consentGiven"),
      description: t("candidates.gdprStatus.desc.consentGiven"),
      bg: "#d4edda",
      color: "#155724",
      icon: <Shield size={12} />,
    },
    withdrawn: {
      label: t("candidates.gdprStatus.withdrawn"),
      description: t("candidates.gdprStatus.desc.withdrawn"),
      bg: "#f8d7da",
      color: "#721c24",
      icon: <AlertTriangle size={12} />,
    },
    deleted: {
      label: t("candidates.gdprStatus.dataDeleted"),
      description: t("candidates.gdprStatus.desc.dataDeleted"),
      bg: "#d6d8db",
      color: "#383d41",
      icon: <Trash2 size={12} />,
    },
    grandfathered: {
      label: t("candidates.gdprStatus.grandfathered"),
      description: t("candidates.gdprStatus.desc.grandfathered"),
      bg: "#dbeafe",
      color: "#1e40af",
      icon: <Shield size={12} />,
    },
  };

  const allGdprStatuses = [
    "none",
    "pending",
    "given",
    "withdrawn",
    "deleted",
    "grandfathered",
  ];

  const renderGdprBadge = (status = "none") => {
    const statusKey = status;
    const config = GDPR_STATUS_CONFIG[statusKey] || GDPR_STATUS_CONFIG.none;
    return (
      <div className="gdpr-badge-wrapper">
        <span
          className="gdpr-badge"
          style={{ background: config.bg, color: config.color }}
        >
          {config.icon}
          {config.label}
        </span>
        <div className="gdpr-lifecycle-tooltip">
          <div className="gdpr-lifecycle-tooltip-inner">
            <div className="gdpr-tooltip-title">
              {t("candidates.gdprStatus.lifecycleTitle")}
            </div>
            {allGdprStatuses.map((key) => {
              const s = GDPR_STATUS_CONFIG[key];
              const isCurrent = key === statusKey;
              return (
                <div
                  key={key}
                  className={`gdpr-tooltip-row${isCurrent ? " gdpr-tooltip-row--current" : ""}`}
                >
                  <span
                    className="gdpr-tooltip-dot"
                    style={{
                      backgroundColor: isCurrent ? s.color : "transparent",
                      borderColor: s.color,
                    }}
                  />
                  <div className="gdpr-tooltip-text">
                    <span className="gdpr-tooltip-label">{s.label}</span>
                    <span className="gdpr-tooltip-desc">{s.description}</span>
                  </div>
                  {isCurrent && (
                    <span className="gdpr-tooltip-current">
                      {t("candidates.gdprStatus.current")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Define candidate columns for table view
  const candidateColumns = useMemo<TableColumn[]>(() => {
    const baseColumns: TableColumn[] = [
      {
        id: "name",
        label: t("candidates.form.fields.firstName"),
        render: (_value, row) => {
          const candidate = row as Candidate;
          return (
            `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim() ||
            "-"
          );
        },
      },
      {
        id: "email",
        label: t("candidates.form.fields.email"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "location",
        label: t("candidates.form.fields.location"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "main_skill",
        label: t("candidates.form.fields.mainSkill"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "seniority",
        label: t("candidates.form.fields.seniority"),
        render: (value) => (value as string) || "-",
      },
      {
        id: "role_type",
        label: t("candidates.form.fields.roleType"),
        render: (value) => (value as string) || "-",
      },
    ];

    // Add rate column only if not tech checker
    if (!isTechChecker) {
      baseColumns.push({
        id: "expected_rate",
        label: t("candidates.form.fields.expectedRate"),
        render: (value) =>
          value ? `$${value}${t("misc.perDay", { ns: "common" })}` : "-",
      });
    }

    return baseColumns.concat([
      {
        id: "status",
        label: t("candidates.form.fields.status"),
        render: (value: unknown, row: TableRowData) => {
          const status =
            (value as string) || (row as Candidate)?.decision || "preparation";
          return <CandidateStatusBadge status={status} size="small" />;
        },
      },
      {
        id: "gdpr_consent_status",
        label: t("candidates.detail.tabs.gdpr"),
        render: (_value: unknown, row: TableRowData) => {
          const candidate = row as Candidate;
          return renderGdprBadge(candidate.gdpr_consent_status);
        },
      },
      {
        id: "source",
        label: t("candidates.form.fields.source"),
        render: (value: unknown) => <span>{(value as string) || "-"}</span>,
      },
      {
        id: "tags",
        label: t("candidates.form.fields.tags"),
        render: (_value: unknown, row: TableRowData) => {
          const candidate = row as Candidate;
          const tags = formatTags(candidate.tags);
          if (tags.length === 0) return <span>-</span>;
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {tags.slice(0, 3).map((tag) => (
                <Chip
                  key={`${candidate.id}-${tag}`}
                  label={tag}
                  size="small"
                  variant="outlined"
                />
              ))}
              {tags.length > 3 && (
                <Chip
                  label={`+${tags.length - 3}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          );
        },
      },
    ]);
  }, [isTechChecker, t]);

  const handleRowClick = (row: TableRowData): void => {
    handleView(row.id as number);
  };

  return (
    <div className="candidates-list">
      <div className="candidates-header">
        <div className="header-content">
          <h1>{t("candidates.list.title")}</h1>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <ViewSwitcher
              viewMode={viewMode}
              onViewChange={setViewMode}
              showBoard
            />
            {!isTechChecker && (
              <SplitActionButton
                label={t("candidates.list.newButton")}
                onClick={handleCreate}
                menuItems={[
                  {
                    label: t("candidates.list.importButton"),
                    icon: <Upload size={16} />,
                    onClick: () => navigate("/candidates/import"),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        className={`search-filters${useAISearch ? " search-filters--ai-active" : ""}`}
      >
        <div className="search-section">
          <div className="search-input-group">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={
                useAISearch
                  ? t("candidates.list.aiSearchPlaceholder")
                  : t("candidates.list.searchPlaceholder")
              }
              ariaLabel="Search candidates"
              showClearButton
            />
            <div className="ai-search-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={useAISearch}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setUseAISearch(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
                <span className="toggle-label">
                  <Bot size={16} /> {t("candidates.list.aiSearch")}
                </span>
                <Tooltip
                  title={t("candidates.list.aiSearchTooltip")}
                  placement="bottom"
                  arrow
                >
                  <span className="ai-search-info-icon">
                    <Info size={14} />
                  </span>
                </Tooltip>
              </label>
            </div>
          </div>
        </div>

        <div
          className={`filters-section${useAISearch ? " filters-disabled" : ""}`}
        >
          <div className="filter-group">
            <FilterSelect
              value={filters.seniority}
              onChange={(value) => handleFilterChange("seniority", value)}
              placeholder={t("candidates.filters.allSeniority")}
              ariaLabel="Filter by seniority"
              options={(
                filterMetadata?.seniorities || [
                  "Junior",
                  "Mid",
                  "Senior",
                  "Lead",
                ]
              ).map((s) => ({ value: s, label: s }))}
            />

            <FilterSelect
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              placeholder={t("candidates.filters.allStatuses")}
              ariaLabel="Filter by status"
              options={[
                {
                  value: "preparation",
                  label: t("candidates.status.preparation"),
                },
                {
                  value: "tech_check",
                  label: t("candidates.status.techCheck"),
                },
                {
                  value: "ready_for_sale",
                  label: t("candidates.status.readyForSale"),
                },
                {
                  value: "to_come_back",
                  label: t("candidates.status.toComeBack"),
                },
                { value: "hired", label: t("candidates.status.hired") },
                { value: "archived", label: t("candidates.status.archived") },
              ]}
            />

            <FilterSelect
              value={filters.main_skill}
              onChange={(value) => handleFilterChange("main_skill", value)}
              placeholder={t("candidates.filters.allSkills")}
              ariaLabel="Filter by skill"
              options={(filterMetadata?.skills || []).map((s) => ({
                value: s,
                label: s,
              }))}
            />

            <FilterSelect
              value={filters.source}
              onChange={(value) => handleFilterChange("source", value)}
              placeholder={t("candidates.filters.allSources")}
              ariaLabel="Filter by source"
              options={(filterMetadata?.sources || []).map((s) => ({
                value: s,
                label: s,
              }))}
            />

            <ClearFiltersButton
              visible={!!hasActiveFilters}
              onClick={clearAllFilters}
            />
          </div>
        </div>
      </div>

      {/* Result count */}
      {!isLoading && !error && (
        <div className="result-count">
          {t("candidates.list.showingResults", {
            count: candidates.length,
            total: candidatesData?.total || candidates.length,
            defaultValue: `Showing ${candidates.length} of ${candidatesData?.total || candidates.length} candidates`,
          })}
        </div>
      )}

      {/* Candidates Grid or Table */}
      {(() => {
        if (isLoading) {
          return (
            <div className="candidates-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="candidate-card skeleton-card">
                  <div className="skeleton-line skeleton-badge" />
                  <div className="skeleton-line skeleton-name" />
                  <div className="skeleton-line skeleton-role" />
                  <div className="skeleton-line skeleton-skill" />
                  <div className="skeleton-line skeleton-summary" />
                  <div className="skeleton-line skeleton-summary short" />
                  <div className="skeleton-line skeleton-footer" />
                </div>
              ))}
            </div>
          );
        }
        if (error) {
          return (
            <ErrorState
              title={t("candidates.errors.loadingCandidates")}
              message={
                (error as Error).message ||
                t("errors.genericError", { ns: "common" })
              }
            />
          );
        }
        if (viewMode === "board") {
          return (
            <StatusKanbanBoard
              items={boardCandidates}
              columns={CANDIDATE_KANBAN_COLUMNS}
              statusConfig={CANDIDATE_STATUS_CONFIG}
              hiddenByDefault={CANDIDATE_BOARD_HIDDEN_BY_DEFAULT}
              archiveColumnKey="archived"
              archiveReasons={CANDIDATE_ARCHIVE_REASONS}
              entityName="Candidate"
              validTransitions={CANDIDATE_VALID_TRANSITIONS}
              onStatusChange={async (itemId, newStatus, note) => {
                await boardStatusMutation.mutateAsync({
                  id: itemId,
                  status: newStatus,
                  note,
                });
              }}
              onArchive={async (itemId, data) => {
                await boardArchiveMutation.mutateAsync({ id: itemId, data });
              }}
              renderCard={(candidate, isDragOverlay) => (
                <CandidateBoardCard
                  key={candidate.id}
                  candidate={candidate}
                  onNavigate={(id) => navigate(`/candidates/${id}`)}
                  isDragOverlay={isDragOverlay}
                />
              )}
              isLoading={boardLoading}
              persistKey="candidates-board-show-completed"
              showDuplicateField
              showGdprWarning
            />
          );
        }
        if (candidates.length === 0) {
          if (useAISearch && debouncedSearch) {
            return (
              <div className="empty-state">
                <h3>
                  {t(
                    "candidates.emptyStates.noAiResults",
                    "No AI search results found",
                  )}
                </h3>
                <p>
                  {t(
                    "candidates.emptyStates.tryDisablingAi",
                    "Try turning off AI Search for broader results",
                  )}
                </p>
                <button
                  className="btn btn-secondary"
                  onClick={() => setUseAISearch(false)}
                >
                  {t("candidates.list.disableAiSearch", "Turn off AI Search")}
                </button>
              </div>
            );
          }
          if (hasActiveFilters) {
            return (
              <div className="empty-state">
                <h3>
                  {t(
                    "candidates.emptyStates.noFilteredResults",
                    "No candidates match your filters",
                  )}
                </h3>
                <p>{t("candidates.emptyStates.tryAdjusting")}</p>
                <button className="btn btn-secondary" onClick={clearAllFilters}>
                  {t("candidates.filters.clearAll", "Clear all filters")}
                </button>
              </div>
            );
          }
          return (
            <EmptyState
              title={t("candidates.emptyStates.noCandidates")}
              message={
                debouncedSearch
                  ? t("candidates.emptyStates.tryAdjusting")
                  : t("candidates.emptyStates.getStarted")
              }
            />
          );
        }
        if (viewMode === "table") {
          return (
            <>
              <TableView
                columns={candidateColumns}
                data={candidates}
                loading={isLoading}
                onRowClick={handleRowClick}
                searchable={false}
                filterable={true}
                selectable={false}
              />
              {/* Pagination */}
              {!useAISearch && candidates.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  hasMore={candidates.length >= 20}
                />
              )}
            </>
          );
        }
        return (
          <>
            <div className="candidates-grid">
              {candidates.map((candidate) => {
                const tags = formatTags(candidate.tags);

                return (
                  <div
                    key={candidate.id}
                    className="candidate-card"
                    onClick={() => handleView(candidate.id)}
                  >
                    {/* Row 1: Badges — status + GDPR left, app count right */}
                    <div className="candidate-badges-row">
                      <div className="candidate-badges-left">
                        {candidate.status && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <CandidateStatusBadgeWithActions
                              candidate={candidate}
                              size="small"
                              disabled={isTechChecker}
                            />
                          </div>
                        )}
                        {renderGdprBadge(candidate.gdpr_consent_status)}
                      </div>
                      <div className="candidate-badges-right">
                        <AppCountDropdown
                          candidateId={candidate.id}
                          count={candidate.active_application_count ?? 0}
                          mostAdvancedStatus={
                            candidate.most_advanced_app_status
                          }
                        />
                      </div>
                    </div>

                    {/* Row 2: Name */}
                    <h3 className="candidate-name">
                      {candidate.first_name} {candidate.last_name}
                      {(candidate as Candidate & { _unindexed?: boolean })
                        ._unindexed && (
                        <span className="unindexed-badge">
                          {t("candidates.list.notAiIndexed", "Not AI-indexed")}
                        </span>
                      )}
                    </h3>

                    {/* Row 3: Seniority + Role */}
                    {(candidate.seniority || candidate.role_type) && (
                      <p className="candidate-role-row">
                        {candidate.seniority}
                        {candidate.seniority && candidate.role_type && " · "}
                        {candidate.role_type}
                      </p>
                    )}

                    {/* Main skill */}
                    {candidate.main_skill && (
                      <div className="candidate-meta-row">
                        <span className="meta-item skill-highlight">
                          {candidate.main_skill}
                        </span>
                      </div>
                    )}

                    {/* Summary preview — 5 lines */}
                    {candidate.summary && (
                      <div className="candidate-summary">
                        <p>{candidate.summary.substring(0, 500)}</p>
                      </div>
                    )}

                    {/* Location — after description */}
                    {candidate.location && (
                      <div className="candidate-location-row">
                        <MapPin size={14} /> {candidate.location}
                      </div>
                    )}

                    {/* Tags — expandable on hover */}
                    {tags.length > 0 && (
                      <div className="candidate-tags">
                        {tags.map((tag) => (
                          <span
                            key={`card-${candidate.id}-${tag}`}
                            className="skill-tag"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Footer: Owner + Recruiter + Calendar + Public Profile */}
                    <div className="candidate-footer-meta">
                      <div className="footer-left">
                        <span
                          className="personnel-label"
                          title={`Created by: ${candidate.owner_name || t("candidates.list.unassigned")}`}
                        >
                          Created by:{" "}
                          {candidate.owner_name ||
                            t("candidates.list.unassigned")}
                        </span>
                        {candidate.responsible_recruiter_name && (
                          <span
                            className="personnel-label"
                            title={`Recruiter: ${candidate.responsible_recruiter_name}`}
                          >
                            Recruiter: {candidate.responsible_recruiter_name}
                          </span>
                        )}
                        {candidate.kam_names && (
                          <span
                            className="personnel-label"
                            title={`KAM: ${candidate.kam_names}`}
                          >
                            KAM: {candidate.kam_names}
                          </span>
                        )}
                        <button
                          className="calendar-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCalendarAnchor({
                              el: e.currentTarget,
                              candidate,
                            });
                          }}
                          aria-label={t(
                            "candidates.list.viewDates",
                            "View dates",
                          )}
                        >
                          <Calendar size={14} />
                        </button>
                      </div>
                      <div
                        className="footer-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {candidate.candidate_code && (
                          <button
                            type="button"
                            className="public-profile-link"
                            title={t("candidates.list.publicProfile")}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const result =
                                  await recruitingAPI.generateShareLink(
                                    candidate.id,
                                  );
                                let shareUrl = result?.url;
                                if (shareUrl) {
                                  try {
                                    const parsed = new URL(shareUrl);
                                    shareUrl = `${window.location.origin}${parsed.pathname}${parsed.search}`;
                                  } catch {
                                    // keep original URL if parsing fails
                                  }
                                }
                                if (!shareUrl) {
                                  console.error(
                                    "Share link response missing URL:",
                                    result,
                                  );
                                  alert(
                                    "Share link was generated but the URL was empty. Please try again.",
                                  );
                                  return;
                                }
                                try {
                                  await navigator.clipboard.writeText(shareUrl);
                                  alert(
                                    "Share link copied! Valid for 72 hours.",
                                  );
                                } catch (clipErr) {
                                  console.warn(
                                    "Clipboard write failed:",
                                    clipErr,
                                  );
                                  prompt(
                                    "Copy this share link (valid for 72 hours):",
                                    shareUrl,
                                  );
                                }
                              } catch (err) {
                                console.error(
                                  "Failed to generate share link:",
                                  err,
                                );
                                alert("Failed to generate share link.");
                              }
                            }}
                          >
                            <ExternalLink size={14} />{" "}
                            {t("candidates.list.publicProfile")}
                          </button>
                        )}
                        {!!candidate.similarity_score && (
                          <span className="similarity-score">
                            {Math.round(candidate.similarity_score * 100)}%{" "}
                            {t("candidates.list.matchScore")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {!useAISearch && candidates.length > 0 && (
              <Pagination
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                hasMore={candidates.length >= 20}
              />
            )}
          </>
        );
      })()}

      {/* Calendar Popover */}
      <Popover
        open={Boolean(calendarAnchor)}
        anchorEl={calendarAnchor?.el}
        onClose={() => setCalendarAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClick={(e) => e.stopPropagation()}
      >
        {calendarAnchor && (
          <div className="calendar-popover">
            <div className="calendar-popover-row">
              <span className="calendar-popover-label">
                {t("candidates.list.created", "Created")}
              </span>
              <span>
                {calendarAnchor.candidate.created_at
                  ? new Date(
                      calendarAnchor.candidate.created_at,
                    ).toLocaleDateString()
                  : "-"}
              </span>
            </div>
            <div className="calendar-popover-row">
              <span className="calendar-popover-label">
                {t("candidates.list.lastActivity", "Last activity")}
              </span>
              <span>
                {calendarAnchor.candidate.updated_at
                  ? new Date(
                      calendarAnchor.candidate.updated_at,
                    ).toLocaleDateString()
                  : "-"}
              </span>
            </div>
          </div>
        )}
      </Popover>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="modal-overlay"
          onClick={handleCancel}
          onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
            if (e.key === "Escape") handleCancel();
          }}
          role="none"
          tabIndex={-1}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <dialog
            className="modal large-modal"
            onClick={(e) => e.stopPropagation()}
            aria-modal="true"
            aria-label="Candidate form"
            open
          >
            <CandidateForm
              candidate={editingCandidate}
              onSave={handleSave}
              onCancel={handleCancel}
              onDelete={async (candidateId: number) => {
                await handleDelete(
                  candidateId,
                  editingCandidate
                    ? `${editingCandidate.first_name} ${editingCandidate.last_name}`
                    : "",
                );
                handleCancel();
              }}
            />
          </dialog>
        </div>
      )}
    </div>
  );
};

export default CandidatesList;
